import React, { useState } from 'react';
import { Check, Gift, ArrowRight } from 'lucide-react';
import { BUNDLE_PACKAGES, BUNDLE_PLANS } from '../data/seoGeoData';
import { trackEvent } from '../lib/track';

const gbp = (n) => `£${n.toLocaleString('en-GB')}`;
const incVat = (n) => Math.round(n * 1.2);

export function BundleBanner() {
  const goToBundle = () => {
    trackEvent('bundle_banner_click', { location: 'packages' });
    document.querySelector('#bundle')?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <section className="pp-section" style={{ paddingTop: 0 }}>
      <div className="pp-container">
        <div className="bb-banner" data-testid="bundle-banner">
          <span className="bb-banner-icon"><Gift size={22} /></span>
          <div className="bb-banner-copy">
            <strong>Bundle and save.</strong> Add an SEO + GEO Growth plan to any website build and
            we will waive the growth plan setup fee — a saving of up to £795 + VAT.
          </div>
          <button className="pp-btn pp-btn-primary pp-btn-sm" onClick={goToBundle} data-testid="bundle-banner-cta">
            Build your bundle <ArrowRight size={15} />
          </button>
        </div>
      </div>
    </section>
  );
}

export default function BundleBuilder() {
  const [pkg, setPkg] = useState(BUNDLE_PACKAGES[1]);
  const [plan, setPlan] = useState(BUNDLE_PLANS[1]);

  const pick = (type, item) => {
    if (type === 'package') setPkg(item); else setPlan(item);
    trackEvent('bundle_builder_select', { [type]: item.id });
  };

  const enquire = () => {
    trackEvent('bundle_enquiry_click', { package: pkg.id, plan: plan.id, saving: plan.setupEx });
    window.dispatchEvent(new CustomEvent('boost-prefill-enquiry', {
      detail: {
        project_type: 'Website plus SEO and GEO',
        details: `I'm interested in the bundle: ${pkg.label} with the SEO + GEO ${plan.label} growth plan (setup fee waived).`,
      },
    }));
    document.querySelector('#enquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="pp-section" id="bundle" data-testid="bundle-builder">
      <div className="pp-container">
        <div className="pp-section-head">
          <h2 className="pp-h2 pp-display">BUILD AND GROW <em>TOGETHER.</em></h2>
          <p className="pp-lead">
            Combine a website build with an ongoing growth plan and we will waive the growth plan
            setup fee. Choose a combination to see the bundle at a glance.
          </p>
        </div>
        <div className="bb-grid">
          <div className="bb-options">
            <span className="bb-label">1 · Choose your website build</span>
            <div className="bb-pills" data-testid="bundle-packages">
              {BUNDLE_PACKAGES.map((p) => (
                <button
                  key={p.id}
                  className={`bb-pill ${pkg.id === p.id ? 'bb-pill-active' : ''}`}
                  onClick={() => pick('package', p)}
                  data-testid={`bundle-pkg-${p.id}`}
                >
                  <span className="bb-pill-name">{p.label}</span>
                  <span className="bb-pill-price">From {gbp(p.priceEx)} + VAT</span>
                </button>
              ))}
            </div>
            <span className="bb-label" style={{ marginTop: 26 }}>2 · Choose your growth plan</span>
            <div className="bb-pills" data-testid="bundle-plans">
              {BUNDLE_PLANS.map((p) => (
                <button
                  key={p.id}
                  className={`bb-pill ${plan.id === p.id ? 'bb-pill-active' : ''}`}
                  onClick={() => pick('plan', p)}
                  data-testid={`bundle-plan-${p.id}`}
                >
                  <span className="bb-pill-name">SEO + GEO {p.label}{p.recommended ? ' ★' : ''}</span>
                  <span className="bb-pill-price">{gbp(p.monthlyEx)} + VAT / month</span>
                </button>
              ))}
            </div>
          </div>
          <div className="bb-summary" data-testid="bundle-summary">
            <span className="bb-summary-kicker">Your bundle</span>
            <h3 className="pp-display bb-summary-title">{pkg.label} + SEO + GEO {plan.label}</h3>
            <dl className="bb-lines">
              <div>
                <dt>Website build (one-off)</dt>
                <dd data-testid="bundle-build-price">From {gbp(pkg.priceEx)} + VAT</dd>
              </div>
              <div>
                <dt>Growth plan setup (one-off)</dt>
                <dd data-testid="bundle-setup-price">
                  <s>{gbp(plan.setupEx)}</s> <span className="bb-free">£0 — waived</span>
                </dd>
              </div>
              <div>
                <dt>Growth plan (monthly)</dt>
                <dd data-testid="bundle-monthly-price">{gbp(plan.monthlyEx)} + VAT</dd>
              </div>
            </dl>
            <div className="bb-saving" data-testid="bundle-saving">
              <Check size={16} /> Bundle saving: <strong>{gbp(plan.setupEx)} + VAT</strong> (setup fee waived)
            </div>
            <div className="bb-totals">
              <div>
                <span className="bb-total-label">One-off from</span>
                <strong data-testid="bundle-total-oneoff">{gbp(pkg.priceEx)} + VAT</strong>
                <small>{gbp(incVat(pkg.priceEx))} including VAT</small>
              </div>
              <div>
                <span className="bb-total-label">Then monthly</span>
                <strong data-testid="bundle-total-monthly">{gbp(plan.monthlyEx)} + VAT</strong>
                <small>{gbp(incVat(plan.monthlyEx))} including VAT</small>
              </div>
            </div>
            <button className="pp-btn pp-btn-primary" style={{ width: '100%' }} onClick={enquire} data-testid="bundle-enquire">
              Enquire about this bundle
            </button>
            <p className="bb-note">
              Prices are starting points. Every bundle is individually scoped and confirmed in a
              written proposal before work begins.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
